// Dr.Rx™ Point-of-Care Clinical Dashboard
import React, { useState, useEffect } from 'react';
import type { Alert, ChatSession, AnswerLength, View, Activity, Platform, MaintenanceConfig, SubscriptionPlan, ClinicalPearl } from '../types'; 
import { getNationalAlerts, getMockNationalAlerts } from '../services/alertService';
import { getMaintenanceConfig } from '../services/adminService'; 
import { useAuth } from '../contexts/AuthContext';
import { allToolsMap } from '../data/toolsData';
import AlertDetailsModal from './AlertDetailsModal';
import { getTodayPearl } from '../services/pearlService';
import { generateClinicalPearl } from '../services/geminiService';
import SEO from './SEO';

// --- Icons ---
const LogoIcon = () => (
  <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="5" fill="#FACC15" className="logo-glow"/>
    <line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="7" r="2" fill="white"/>
    <line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="41" r="2" fill="white"/>
    <line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="7" cy="24" r="2" fill="white"/>
    <line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="41" cy="24" r="2" fill="white"/>
  </svg>
);

const SendIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" /></svg>;
const ClockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const ArrowRightIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;
const BellIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>;
const SparklesIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>;
const GridIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>;
const ListIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>;
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>;
const PowerIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>;

const NATIONAL_ALERT_SOURCES = ['FDA', 'CDC', 'DEA'];

const FdaIcon = () => <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const CdcIcon = () => <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" stroke="currentColor" strokeWidth="2"><path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>;
const DeaIcon = () => <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" stroke="currentColor" strokeWidth="2"><path d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" /></svg>;
const DefaultAlertIcon = () => <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" stroke="currentColor" strokeWidth="2"><path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;

const AlertTicker: React.FC<{ 
    alerts: Alert[], 
    isLoading: boolean, 
    onOpenModal: (alert: Alert) => void,
    emptyMessage: string
}> = ({ alerts, isLoading, onOpenModal, emptyMessage }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [viewMode, setViewMode] = useState<'Featured' | 'List'>('Featured');

    useEffect(() => {
        if (isLoading || alerts.length === 0 || isPaused || viewMode === 'List') return;
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % alerts.length);
        }, 8000);
        return () => clearInterval(interval);
    }, [alerts.length, isPaused, viewMode, isLoading]);

    if (isLoading) {
        return (
             <div className="h-40 bg-white/5 rounded-xl animate-pulse border border-white/5 flex items-center justify-center">
                 <div className="flex flex-col items-center gap-2">
                     <div className="h-5 w-5 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                     <span className="text-xs text-gray-500">Scanning FDA, CDC &amp; DEA feeds...</span>
                 </div>
             </div>
        );
    }

    if (alerts.length === 0) {
        return <div className="p-6 text-center text-gray-500 text-sm border border-white/5 rounded-xl bg-black/20">{emptyMessage}</div>;
    }

    const currentAlert = alerts[currentIndex] || alerts[0];
    
    const getSourceTheme = (source: string) => {
        const s = source.toUpperCase();
        if (s.includes('FDA')) return { iconColor: 'text-blue-400', badgeClass: 'bg-blue-900/50 text-blue-300 border-blue-700/50', progressColor: 'bg-blue-500', Icon: FdaIcon };
        if (s.includes('CDC')) return { iconColor: 'text-teal-400', badgeClass: 'bg-teal-900/50 text-teal-300 border-teal-700/50', progressColor: 'bg-teal-500', Icon: CdcIcon };
        if (s.includes('DEA')) return { iconColor: 'text-purple-400', badgeClass: 'bg-purple-900/50 text-purple-300 border-purple-700/50', progressColor: 'bg-purple-500', Icon: DeaIcon };
        return { iconColor: 'text-gray-400', badgeClass: 'bg-gray-800 text-gray-300 border-gray-700', progressColor: 'bg-gray-500', Icon: DefaultAlertIcon };
    };

    const theme = getSourceTheme(currentAlert.source);
    const severityBadgeColor = currentAlert.severity === 'High' ? 'bg-red-500 text-white border border-red-600' : currentAlert.severity === 'Medium' ? 'bg-yellow-500 text-black border border-yellow-600' : 'bg-green-600 text-white border border-green-700';

    return (
        <div className="relative">
            <div className="flex justify-between items-center mb-3 px-1">
                 <div className="flex gap-1.5">
                    {alerts.map((_, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => setCurrentIndex(idx)}
                            className={`h-1.5 rounded-full transition-all duration-500 ${idx === currentIndex ? `w-6 ${theme.progressColor}` : 'w-1.5 bg-gray-700 hover:bg-gray-500'}`}
                            aria-label={`Alert ${idx + 1}`}
                        />
                    ))}
                 </div>
                 <div className="flex gap-2">
                     <button onClick={() => setViewMode('Featured')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'Featured' ? 'text-yellow-400 bg-white/10' : 'text-gray-500 hover:text-gray-300'}`} title="Carousel View"><SparklesIcon /></button>
                     <button onClick={() => setViewMode('List')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'List' ? 'text-yellow-400 bg-white/10' : 'text-gray-500 hover:text-gray-300'}`} title="List View"><ListIcon /></button>
                 </div>
            </div>

            {viewMode === 'Featured' ? (
                <div 
                    className="relative overflow-hidden rounded-2xl border border-blue-900/40 bg-[#1E2E4A] transition-all duration-300 cursor-pointer group hover:border-yellow-400/40"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    onClick={() => onOpenModal(currentAlert)}
                >
                    {!isPaused && (
                        <div className={`absolute top-0 left-0 h-[2px] z-20 animate-progress ${theme.progressColor}`} style={{ width: '100%', animationDuration: '8000ms' }} key={currentIndex}></div>
                    )}
                    <div className={`absolute -right-6 -bottom-6 w-32 h-32 opacity-[0.07] pointer-events-none rotate-12 ${theme.iconColor}`}><theme.Icon /></div>

                    <div className="p-5 relative z-10 animate-slide-in" key={currentIndex}>
                        <div className="flex justify-between items-start mb-3">
                            <div className="flex gap-2 items-center">
                                <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border uppercase tracking-wide flex items-center gap-1.5 ${theme.badgeClass}`}>
                                    <span className="w-3 h-3 block"><theme.Icon /></span>
                                    {currentAlert.source}
                                </span>
                                <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${severityBadgeColor}`}>
                                    {currentAlert.severity.toUpperCase()}
                                </span>
                            </div>
                            <span className="text-[10px] text-gray-400 font-mono tabular-nums">
                                {new Date(currentAlert.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                        </div>
                        <h4 className="text-base md:text-lg font-bold text-white leading-tight line-clamp-2 mb-2 group-hover:text-yellow-300 transition-colors">{currentAlert.title}</h4>
                        <p className="text-xs text-gray-300 line-clamp-2 leading-relaxed max-w-[92%]">{currentAlert.summary}</p>
                        <div className="mt-4 flex items-center gap-2 text-[11px] text-yellow-400 font-bold opacity-85 group-hover:opacity-100 transition-all">
                            <span>VIEW CLINICAL BRIEFING</span>
                            <ArrowRightIcon />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-2.5 max-h-[320px] overflow-y-auto custom-scrollbar pr-1">
                    {alerts.map(alert => {
                         const listTheme = getSourceTheme(alert.source);
                         return (
                             <div key={alert.id} onClick={() => onOpenModal(alert)} className="p-3 rounded-xl bg-[#1E2E4A] border border-blue-900/30 hover:border-yellow-400/40 hover:bg-[#253655] cursor-pointer group transition-all relative overflow-hidden">
                                <div className="flex justify-between items-start pl-1">
                                    <div className="flex-grow pr-2">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`w-3 h-3 ${listTheme.iconColor}`}><listTheme.Icon /></span>
                                            <span className={`text-[10px] font-bold uppercase tracking-wider ${listTheme.iconColor}`}>{alert.source}</span>
                                            <span className="text-[10px] text-gray-600">•</span>
                                            <span className="text-[10px] text-gray-500 tabular-nums">{new Date(alert.date).toLocaleDateString()}</span>
                                        </div>
                                        <h4 className="text-xs font-bold text-gray-200 line-clamp-1 group-hover:text-white leading-snug">{alert.title}</h4>
                                    </div>
                                    <span className={`text-[9px] px-2 py-0.5 rounded-md font-bold flex-shrink-0 border ${alert.severity === 'High' ? 'bg-red-500/10 text-red-300 border-red-500/20' : 'bg-gray-700/30 text-gray-400 border-gray-600/30'}`}>
                                        {alert.severity}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const PlanStatusWidget: React.FC<{ plan: SubscriptionPlan, onNavigate: (view: View) => void, onSignOut: () => void }> = ({ plan, onNavigate, onSignOut }) => {
    const planConfig = {
        Free: { label: 'Starter Plan', icon: '🌱', primaryAction: 'View Plans', primaryActionTarget: 'pricing' as View, gradient: 'bg-gradient-to-r from-blue-600/10 to-blue-400/10', border: 'border-blue-500/20', text: 'text-blue-200', animation: '', glow: '' },
        Trial: { label: 'Trial', icon: '⏳', primaryAction: 'Upgrade Now', primaryActionTarget: 'pricing' as View, gradient: 'bg-gradient-to-r from-gray-800 to-gray-700', border: 'border-gray-600', text: 'text-gray-300', animation: '', glow: '' },
        Plus: { label: 'Plus Member', icon: '⭐', primaryAction: 'Manage Plan', primaryActionTarget: 'subscription-management' as View, gradient: 'bg-gradient-to-r from-indigo-600/20 to-blue-500/20', border: 'border-indigo-500/40', text: 'text-indigo-200', animation: '', glow: 'shadow-[0_0_15px_rgba(99,102,241,0.15)]' },
        Pro: { label: 'PRO MEMBER', icon: '👑', primaryAction: 'Manage Plan', primaryActionTarget: 'subscription-management' as View, gradient: 'bg-gradient-to-r from-amber-200/10 via-yellow-400/10 to-amber-600/10', border: 'border-yellow-500/50', text: 'text-yellow-200', animation: 'relative overflow-hidden', glow: 'shadow-[0_0_20px_rgba(250,204,21,0.2)]' },
        Teams: { label: 'Teams Plan', icon: '🏢', primaryAction: 'Manage Plan', primaryActionTarget: 'subscription-management' as View, gradient: 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10', border: 'border-emerald-500/40', text: 'text-emerald-200', animation: '', glow: 'shadow-[0_0_15px_rgba(16,185,129,0.15)]' }
    };
    const config = planConfig[plan as keyof typeof planConfig] || planConfig.Free;
    const isPro = plan === 'Pro' || plan === 'Teams';
    return (
        <div className={`flex items-center p-1.5 rounded-2xl border backdrop-blur-md transition-all duration-300 ${config.gradient} ${config.border} ${config.glow} ${config.animation}`}>
             {isPro && <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent z-0 pointer-events-none"></div>}
             <div className="relative z-10 flex items-center gap-3 pr-2 pl-3">
                 <div className="flex flex-col">
                     <span className={`text-[10px] uppercase tracking-widest font-bold ${config.text} opacity-80`}>Current Plan</span>
                     <div className="flex items-center gap-1.5">
                         <span className="text-sm">{config.icon}</span>
                         <span className={`font-bold text-sm ${isPro ? 'text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-amber-400' : 'text-white'}`}>{config.label}</span>
                     </div>
                 </div>
                 <div className="w-px h-8 bg-white/10 mx-1"></div>
                 <div className="flex items-center gap-2">
                     <button onClick={() => onNavigate(config.primaryActionTarget)} className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${isPro ? 'bg-yellow-400/20 text-yellow-200 hover:bg-yellow-400/30 border border-yellow-400/30' : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'}`}>{config.primaryAction}</button>
                     <button onClick={onSignOut} className="p-1.5 text-gray-400 hover:text-red-400 transition-colors rounded-lg hover:bg-white/5" title="Sign Out"><PowerIcon /></button>
                 </div>
             </div>
        </div>
    );
};

const MaintenanceBanner: React.FC<{ config: MaintenanceConfig }> = ({ config }) => {
    if (!config.isActive) return null;
    const colors = { info: 'bg-blue-900/90 border-blue-500 text-white', warning: 'bg-yellow-900/90 border-yellow-500 text-yellow-100', error: 'bg-red-900/90 border-red-500 text-white' };
    const icons = { info: 'ℹ️', warning: '⚠️', error: '🚨' };
    return (
        <div className={`mb-6 p-4 rounded-xl border-l-4 shadow-lg animate-fade-in-down flex items-start gap-3 ${colors[config.type]}`}>
            <span className="text-2xl">{icons[config.type]}</span>
            <div><h3 className="font-bold text-sm uppercase tracking-wider mb-1">System Notice</h3><p className="text-sm font-medium leading-relaxed">{config.message}</p></div>
        </div>
    );
};

interface DashboardProps {
  onEvidenceSearch: (query: string) => void;
  isVoiceEnabled: boolean;
  isPushEnabled: boolean;
  isPwaInstallable: boolean;
  isPwaInstalled: boolean;
  onPwaInstall: () => void;
  platform: Platform;
  quickAccessTools: string[];
  onOpenEditQuickAccess: () => void;
  setActiveTool: (toolName: string | null) => void;
  onDashboardSearch: (query: string) => void;
  recentActivities: Activity[];
  onClearActivity: () => void;
  sessions: ChatSession[];
  onNavigate: (view: View) => void;
  onSelectSession: (id: string | null) => void;
  onSelectDrug: (id: string) => Promise<void>;
  analytics: { totalChats: number; toolsUsed: number; customDrugs: number; };
  onActivityClick: (activity: Activity) => void;
  onSendMessageFromDashboard: (message: string, length: AnswerLength) => void;
  onSignUp?: () => void;
}

const GlassCard: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className }) => (
    <div className={`bg-[#15243E]/70 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl ${className || ''}`}>{children}</div>
);

const QUICK_CLINICAL_CHIPS = [
    { label: 'Renal Dosing Check', prompt: 'What are the Cockcroft-Gault renal dosing adjustments and monitoring parameters for ' },
    { label: 'IV Y-Site Compatibility', prompt: 'Check Y-site IV compatibility, concentration limits, and line flushing protocol for ' },
    { label: 'CYP450 / P-gp DDI', prompt: 'Analyze clinically significant CYP450 and P-gp drug-drug interactions and management for ' },
    { label: 'Anticoag Bridging', prompt: 'Provide perioperative anticoagulation hold and bridging recommendations for ' },
];

const Dashboard: React.FC<DashboardProps> = (props) => {
    const { user, subscription, isAlertFallbackActive, logout } = useAuth();
    const [chatInput, setChatInput] = useState('');
    const [answerLength, setAnswerLength] = useState<AnswerLength>('short');
    const [nationalAlerts, setNationalAlerts] = useState<Alert[]>([]);
    const [isLoadingAlerts, setIsLoadingAlerts] = useState(true);
    const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
    const [maintenanceConfig, setMaintenanceConfig] = useState<MaintenanceConfig | null>(null);
    const [pearl, setPearl] = useState<ClinicalPearl | null>(null);

    const getTimeBasedGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 5) return { text: 'Good Late Night', icon: '🌙' };
        if (hour < 12) return { text: 'Good Morning', icon: '☀️' };
        if (hour < 17) return { text: 'Good Afternoon', icon: '🌤️' };
        return { text: 'Good Evening', icon: '🌙' };
    };
    const { text: greetingText, icon: greetingIcon } = getTimeBasedGreeting();

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const config = await getMaintenanceConfig();
                setMaintenanceConfig(config);
            } catch (e) {
                console.error("Failed to load maintenance config", e);
            }
        };
        fetchConfig();
    }, []);

    useEffect(() => {
        const fetchAlerts = async () => {
            setIsLoadingAlerts(true);
            try {
                const { alerts: natAlerts } = await getNationalAlerts(NATIONAL_ALERT_SOURCES, isAlertFallbackActive);
                const finalNational = [...natAlerts];
                finalNational.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                setNationalAlerts(finalNational.slice(0, 10));
            } catch (error) {
                console.error("Alert Fetch Error", error);
                const { alerts } = await getMockNationalAlerts();
                setNationalAlerts(alerts.slice(0, 5));
            } finally {
                setIsLoadingAlerts(false);
            }
        };
        fetchAlerts();
    }, [isAlertFallbackActive]);

    useEffect(() => {
        const loadPearl = async () => {
            try {
                const dbPearl = await getTodayPearl();
                if (dbPearl) {
                    setPearl(dbPearl);
                } else {
                    const localPearl = localStorage.getItem('drrx_local_pearl_cache');
                    const localPearlDate = localStorage.getItem('drrx_local_pearl_date');
                    const today = new Date().toDateString();
                    if (localPearl && localPearlDate === today) {
                        setPearl(JSON.parse(localPearl));
                    } else {
                        const text = await generateClinicalPearl();
                        const fallbackPearl: ClinicalPearl = { topic: 'Daily Clinical Insight', content: text, category: 'General', source: 'AI Generated', status: 'PUBLISHED', scheduledDate: new Date().toISOString() };
                        setPearl(fallbackPearl);
                        localStorage.setItem('drrx_local_pearl_cache', JSON.stringify(fallbackPearl));
                        localStorage.setItem('drrx_local_pearl_date', today);
                    }
                }
            } catch (e) { console.error("Failed to load pearl", e); }
        };
        loadPearl();
    }, []);

    const handleChatSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (chatInput.trim()) {
            props.onSendMessageFromDashboard(chatInput, answerLength);
        }
    };

    const handleOpenAlertModal = (alert: Alert) => setSelectedAlert(alert);
    
    const interactionsLeft = subscription?.interactionLimit === 'unlimited' ? 'Unlimited' : subscription?.interactionsLeft;
    const interactionLimit = subscription?.interactionLimit === 'unlimited' ? '' : subscription?.interactionLimit;
    const isTrial = subscription?.plan === 'Trial';
    const showInstallButton = (props.isPwaInstallable || props.platform === 'iOS') && !props.isPwaInstalled;

    return (
        <div className="relative w-full min-h-screen">
            <SEO title="Dr.Rx™ - AI Clinical Pharmacist Co-Pilot Dashboard" description="Point-of-care AI Clinical Pharmacist Co-Pilot, 40+ calculators, and FDA/CDC/DEA alerts." url="/?view=dashboard" keywords={["Clinical Pharmacy Dashboard", "AI Medical Assistant", "Dr.Rx"]} />

            {/* HEADER GREETING */}
            <div className="max-w-7xl mx-auto pt-6 px-4 sm:px-6 lg:px-8 mb-6">
                <header className="relative bg-[#15243E]/60 backdrop-blur-xl border border-blue-900/40 rounded-3xl p-6 sm:p-8 flex flex-col lg:flex-row justify-between items-center lg:items-start gap-6 shadow-2xl overflow-hidden group transition-all hover:border-yellow-400/20">
                    <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-all duration-1000 pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-yellow-500/5 rounded-full blur-3xl group-hover:bg-yellow-500/10 transition-all duration-1000 pointer-events-none"></div>
                    <div className="relative z-10 flex items-center gap-5 w-full lg:w-auto">
                        <div className="p-4 bg-[#0F172A] rounded-2xl border border-blue-900/40 shadow-lg group-hover:scale-105 transition-transform duration-500 flex-shrink-0"><LogoIcon /></div>
                        <div className="text-left">
                            <h1 className="text-2xl sm:text-4xl font-bold text-white tracking-tight mb-1.5">
                                <span className="block font-light text-gray-300 text-lg sm:text-2xl mb-0.5">{greetingText},</span>
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 via-yellow-400 to-amber-500 drop-shadow-sm">{user?.name?.split(' ')[0] || 'Clinician'}</span>
                            </h1>
                            <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                                Your <span className="text-yellow-400 font-semibold">AI Clinical Pharmacist Co-Pilot</span> &amp; point-of-care suite are active.
                            </p>
                        </div>
                    </div>
                    <div className="relative z-10 w-full lg:w-auto flex flex-col items-center lg:items-end gap-3 mt-2 lg:mt-0">
                        <PlanStatusWidget plan={subscription?.plan || 'Free'} onNavigate={props.onNavigate} onSignOut={logout} />
                        {showInstallButton && <button onClick={props.onPwaInstall} className="flex items-center justify-center gap-2 text-xs font-bold text-yellow-400 bg-yellow-400/10 border border-yellow-400/30 hover:bg-yellow-400/20 px-4 py-2 rounded-xl transition-all transform hover:scale-105 shadow-sm backdrop-blur-sm w-full lg:w-auto"><DownloadIcon /><span>Install App</span></button>}
                    </div>
                    <div className="absolute right-10 top-6 text-6xl opacity-5 transform rotate-12 pointer-events-none select-none hidden lg:block">{greetingIcon}</div>
                </header>
            </div>

            {/* AI CLINICAL CO-PILOT HERO LAUNCHER */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
                <div className="bg-gradient-to-r from-[#15243E] via-[#1B2E4F] to-[#15243E] border border-yellow-400/30 rounded-2xl p-5 sm:p-6 shadow-2xl relative overflow-hidden">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-yellow-400/15 border border-yellow-400/30 text-yellow-300 text-[10px] font-bold uppercase tracking-wider mb-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse"></span>
                                Primary Clinical Engine
                            </div>
                            <h2 className="text-lg sm:text-xl font-bold text-white">Dr.Rx™ AI Clinical Pharmacist Co-Pilot</h2>
                            <p className="text-xs sm:text-sm text-gray-300 mt-0.5">
                                Role-calibrated answers (Pharmacist, Physician, Nurse, Researcher), inline drug monographs, and 1-click clinical note export.
                            </p>
                        </div>
                        <button
                            onClick={() => props.onNavigate('chat')}
                            className="flex-shrink-0 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 text-slate-950 font-bold text-xs sm:text-sm px-5 py-3 rounded-xl shadow-lg shadow-yellow-400/15 transition-all transform hover:scale-[1.02] active:scale-95"
                        >
                            <span>Open Full Clinical Co-Pilot</span>
                            <ArrowRightIcon />
                        </button>
                    </div>
                    <div className="mt-4 pt-3 border-t border-white/10 flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-1">Quick Consult:</span>
                        {QUICK_CLINICAL_CHIPS.map((chip) => (
                            <button
                                key={chip.label}
                                type="button"
                                onClick={() => setChatInput(chip.prompt)}
                                className="text-[11px] font-semibold text-blue-200 hover:text-yellow-300 bg-[#0F172A]/80 hover:bg-[#0F172A] border border-blue-800/50 hover:border-yellow-400/40 px-3 py-1.5 rounded-lg transition-all"
                            >
                                + {chip.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* MAIN GRID */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-40 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                <div className="lg:col-span-8 space-y-6 lg:space-y-8">
                    {maintenanceConfig && <MaintenanceBanner config={maintenanceConfig} />}

                    {/* QUICK ACTIONS */}
                    <section>
                        <div className="flex justify-between items-center mb-4 px-1">
                            <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2"><span className="text-yellow-400">⚡</span> Point-of-Care Quick Tools</h2>
                            <div className="flex gap-2">
                                <button onClick={() => props.onNavigate('all-tools')} className="text-[10px] text-gray-300 hover:text-white font-semibold uppercase tracking-wide border border-gray-700 hover:border-gray-500 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"><GridIcon /> All 40+ Tools</button>
                                <button onClick={props.onOpenEditQuickAccess} className="text-[10px] text-yellow-400 hover:text-yellow-300 font-semibold uppercase tracking-wide border border-yellow-400/30 px-3 py-1.5 rounded-lg transition-colors hover:bg-yellow-400/10">Customize</button>
                            </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2 sm:gap-4">
                            {props.quickAccessTools.map(toolName => {
                                const tool = allToolsMap.get(toolName);
                                if (!tool) return null;
                                const IconComponent = tool.icon;
                                return (
                                    <button key={toolName} onClick={() => props.setActiveTool(tool.name)} className="relative group flex flex-col items-center justify-start p-3 rounded-xl bg-[#1E2E4A]/50 backdrop-blur-sm border border-white/10 hover:border-yellow-400/40 hover:bg-[#1E2E4A] transition-all duration-300 active:scale-95 h-auto min-h-[84px]">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 flex items-center justify-center mb-2 group-hover:scale-110 group-hover:border-yellow-400/30 transition-all duration-300 shadow-inner">
                                            <div className="text-yellow-400 filter drop-shadow-md"><IconComponent className="h-5 w-5 sm:h-6 sm:w-6" /></div>
                                        </div>
                                        <span className="text-[10px] sm:text-xs text-gray-200 text-center font-bold uppercase tracking-wide leading-tight group-hover:text-white transition-colors line-clamp-2 w-full">{tool.name}</span>
                                    </button>
                                );
                            })}
                            {props.quickAccessTools.length < 8 && (
                                <button onClick={props.onOpenEditQuickAccess} className="flex flex-col items-center justify-start p-3 rounded-xl border border-dashed border-white/15 hover:border-yellow-400/40 hover:bg-white/5 transition-all group active:scale-95 h-auto min-h-[84px]">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 text-gray-500 group-hover:text-yellow-400 transition-colors"><PlusIcon /></div>
                                    <span className="text-[10px] sm:text-xs text-gray-400 font-bold uppercase tracking-wide">Add Tool</span>
                                </button>
                            )}
                        </div>
                    </section>

                    {/* NATIONAL REGULATORY ALERTS WIDGET */}
                    <GlassCard>
                        <div className="p-5">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2">
                                    <span className="text-yellow-400"><BellIcon /></span>
                                    FDA, CDC &amp; DEA Safety Alerts
                                </h2>
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-blue-950/80 border border-blue-800/60 text-blue-300">
                                    National Surveillance
                                </span>
                            </div>
                            <div className="min-h-[180px]">
                                <AlertTicker
                                    alerts={nationalAlerts}
                                    isLoading={isLoadingAlerts}
                                    onOpenModal={handleOpenAlertModal}
                                    emptyMessage="No critical national alerts at this time."
                                />
                            </div>
                        </div>
                    </GlassCard>
                </div>

                {/* RIGHT COLUMN */}
                <div className="lg:col-span-4 space-y-6 lg:space-y-8">
                    {/* CLINICAL PEARL */}
                    {pearl && (
                        <section className="animate-fade-in-up delay-100">
                            <div className="bg-gradient-to-br from-[#1B2B4B] to-[#15243E] backdrop-blur-xl border border-yellow-400/25 rounded-2xl p-6 relative overflow-hidden shadow-2xl group hover:border-yellow-400/40 transition-colors">
                                 <div className="absolute top-0 right-0 p-4 opacity-10 text-7xl rotate-12 group-hover:opacity-20 transition-opacity">💡</div>
                                 <div className="relative z-10">
                                     <div className="flex justify-between items-start mb-2">
                                        <h3 className="text-yellow-400 font-bold text-xs uppercase tracking-wider flex items-center gap-1.5"><SparklesIcon /> Daily Clinical Pearl</h3>
                                        <span className="text-[11px] text-gray-400 bg-black/30 px-2 py-0.5 rounded-lg tabular-nums">{pearl.scheduledDate ? new Date(pearl.scheduledDate).toLocaleDateString() : 'Today'}</span>
                                     </div>
                                     <h4 className="text-white font-bold text-lg mb-2">{pearl.topic}</h4>
                                     <p className="text-gray-200 text-sm leading-relaxed mb-4">{pearl.content}</p>
                                     <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                                        <span className="bg-black/30 px-2.5 py-1 rounded-md border border-white/10 text-blue-200 font-medium">{pearl.category}</span>
                                        {pearl.source && <span>Source: {pearl.source}</span>}
                                        {pearl.sourceUrl && <a href={pearl.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300 underline">Reference ↗</a>}
                                     </div>
                                 </div>
                            </div>
                        </section>
                    )}

                    {/* RECENT ACTIVITY */}
                    <GlassCard>
                        <div className="p-5">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2"><ClockIcon /> Recent Clinical Activity</h2>
                                {props.recentActivities.length > 0 && <button onClick={props.onClearActivity} className="text-[10px] text-red-400 hover:text-red-300 font-semibold bg-red-500/10 hover:bg-red-500/20 px-2.5 py-1 rounded-md transition-colors">Clear</button>}
                            </div>
                            <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                                {props.recentActivities.length > 0 ? (
                                     <div className="space-y-2">
                                        {props.recentActivities.slice(0, 5).map(activity => (
                                            <button key={activity.id} onClick={() => props.onActivityClick(activity)} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors text-left group border border-transparent hover:border-white/10">
                                                <div className="flex items-center gap-3 overflow-hidden">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0 group-hover:scale-125 transition-transform"></div>
                                                    <div className="min-w-0">
                                                        <p className="text-gray-200 font-medium text-sm truncate group-hover:text-white transition-colors">{activity.text}</p>
                                                        <p className="text-[10px] text-gray-500 tabular-nums">{new Date(activity.timestamp).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                                <ArrowRightIcon />
                                            </button>
                                        ))}
                                    </div>
                                ) : <div className="p-6 text-center text-gray-500 text-sm">No recent activity yet. Start an AI consultation or open a clinical calculator!</div>}
                            </div>
                        </div>
                    </GlassCard>
                </div>
            </div>

            {/* FLOATING AI CLINICAL CO-PILOT BAR */}
            <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] left-0 right-0 z-20 px-4 pb-4 pt-4 pointer-events-none">
                <div className="max-w-3xl mx-auto pointer-events-auto">
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/90 to-transparent -z-10 h-[150%] bottom-0 transform translate-y-4 blur-md rounded-t-3xl"></div>
                    <form onSubmit={handleChatSubmit} className="relative group">
                        <div className="bg-[#1E2E4A]/95 backdrop-blur-xl rounded-full border border-yellow-400/30 flex items-center p-1.5 shadow-2xl shadow-black/60 transition-all focus-within:border-yellow-400 focus-within:shadow-yellow-400/15 focus-within:bg-[#15243E] ring-1 ring-white/10">
                            <input
                                type="text"
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                placeholder="Ask Dr.Rx™ Clinical Co-Pilot (dosing, CrCl, Y-site, DDIs)..."
                                className="flex-grow bg-transparent text-white text-sm sm:text-base px-5 py-3 rounded-l-full focus:outline-none placeholder-gray-400"
                            />
                            <div className="flex items-center gap-2 pr-1">
                                <div className="hidden sm:flex bg-black/40 rounded-full p-0.5 border border-white/10">
                                    <button type="button" onClick={() => setAnswerLength('short')} className={`px-3 py-1.5 rounded-full text-[10px] font-bold transition-all ${answerLength === 'short' ? 'bg-yellow-400 text-slate-950 shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}>SHORT</button>
                                    <button type="button" onClick={() => setAnswerLength('detailed')} className={`px-3 py-1.5 rounded-full text-[10px] font-bold transition-all ${answerLength === 'detailed' ? 'bg-yellow-400 text-slate-950 shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}>MONOGRAPH</button>
                                </div>
                                <button type="submit" disabled={!chatInput.trim()} className="w-11 h-11 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-gray-900 shadow-lg hover:shadow-yellow-400/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95" aria-label="Send clinical query"><SendIcon /></button>
                            </div>
                        </div>
                    </form>
                    {interactionsLeft !== 'Unlimited' && typeof interactionsLeft === 'number' && (
                        <div className="text-center mt-2.5"><p className="text-[10px] font-medium text-gray-300 bg-black/70 backdrop-blur-md inline-block px-3 py-1 rounded-full border border-white/10 shadow-sm tabular-nums"><span className="text-yellow-400 font-bold">{interactionsLeft}</span> of {interactionLimit} AI consultations remaining {isTrial ? 'for trial' : 'today'}.</p></div>
                    )}
                </div>
            </div>

            {selectedAlert && <AlertDetailsModal alert={selectedAlert} onClose={() => setSelectedAlert(null)} />}
        </div>
    );
};

export default Dashboard;
